import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { DualStickArbiter, type StickSide, type StickVector } from "./dual-stick";

export interface DesktopActions {
  readonly onPreview: () => void;
  readonly onPrimary: () => void;
  readonly onContinue: () => void;
  readonly onCancel: () => void;
  readonly onPause: () => void;
  readonly onMovementChanged: (moving: boolean, source: StickSide | null) => void;
  readonly onMoveRequested: (displacement: Vector3) => void;
}

const MOVEMENT_SPEED_METERS_PER_SECOND = 1.2;

export class DesktopInput {
  private readonly pressed = new Set<string>();
  private readonly arbiter = new DualStickArbiter();
  private enabled = false;
  private wasMoving = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: UniversalCamera,
    private readonly actions: DesktopActions,
  ) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    document.addEventListener("mousemove", this.onMouseMove);
    canvas.addEventListener("click", this.onCanvasClick);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.clearMovement();
  }

  update(deltaSeconds: number): void {
    if (!this.enabled) return;
    this.updateStick("left", this.readLeftStick());
    this.updateStick("right", this.readRightStick());
    const active = this.arbiter.getActive();
    const moving = active.side !== null;

    if (moving !== this.wasMoving) {
      this.wasMoving = moving;
      this.actions.onMovementChanged(moving, active.side);
    }
    if (!moving) return;

    const forward = this.camera.getDirection(Vector3.Forward());
    forward.y = 0;
    forward.normalize();
    const right = Vector3.Cross(Vector3.Up(), forward).normalize();
    const displacement = forward
      .scale(active.vector.y * MOVEMENT_SPEED_METERS_PER_SECOND * deltaSeconds)
      .add(right.scale(active.vector.x * MOVEMENT_SPEED_METERS_PER_SECOND * deltaSeconds));
    this.actions.onMoveRequested(displacement);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    document.removeEventListener("mousemove", this.onMouseMove);
    this.canvas.removeEventListener("click", this.onCanvasClick);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "Escape") {
      event.preventDefault();
      if (!event.repeat) this.actions.onPause();
      return;
    }
    if (!this.enabled) return;
    if (["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)) {
      event.preventDefault();
      if (!this.pressed.has(event.code)) this.markStickForKey(event.code);
      this.pressed.add(event.code);
      return;
    }
    if (event.repeat) return;
    if (event.code === "Space") {
      event.preventDefault();
      this.actions.onPreview();
    } else if (event.code === "KeyE" || event.code === "Enter") {
      event.preventDefault();
      this.actions.onPrimary();
    } else if (event.code === "KeyQ" || event.code === "Backspace") {
      event.preventDefault();
      this.actions.onContinue();
    } else if (event.code === "KeyC") {
      event.preventDefault();
      this.actions.onCancel();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.pressed.delete(event.code);
    this.markStickForKey(event.code);
  };

  private readonly onBlur = (): void => this.clearMovement();

  private readonly onCanvasClick = (): void => {
    if (this.enabled && document.pointerLockElement !== this.canvas) {
      void this.canvas.requestPointerLock();
    }
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.enabled || document.pointerLockElement !== this.canvas) return;
    this.camera.rotation.y += event.movementX * 0.0022;
    this.camera.rotation.x = Math.max(-1.15, Math.min(1.15, this.camera.rotation.x + event.movementY * 0.0022));
  };

  private readLeftStick(): StickVector {
    return {
      x: Number(this.pressed.has("KeyD")) - Number(this.pressed.has("KeyA")),
      y: Number(this.pressed.has("KeyW")) - Number(this.pressed.has("KeyS")),
    };
  }

  private readRightStick(): StickVector {
    return {
      x: Number(this.pressed.has("ArrowRight")) - Number(this.pressed.has("ArrowLeft")),
      y: Number(this.pressed.has("ArrowUp")) - Number(this.pressed.has("ArrowDown")),
    };
  }

  private updateStick(side: StickSide, vector: StickVector): void {
    this.arbiter.update(side, vector, performance.now());
  }

  private markStickForKey(code: string): void {
    const side = code.startsWith("Arrow") ? "right" : "left";
    this.updateStick(side, side === "left" ? this.readLeftStick() : this.readRightStick());
  }

  private clearMovement(): void {
    this.pressed.clear();
    this.arbiter.update("left", { x: 0, y: 0 }, performance.now());
    this.arbiter.update("right", { x: 0, y: 0 }, performance.now());
    if (this.wasMoving) this.actions.onMovementChanged(false, null);
    this.wasMoving = false;
  }
}
